// Sauvegarde automatique du brouillon sur cet appareil (navigateur), pour ne rien perdre
// si l'onglet se ferme ou si la connexion coupe avant « Enregistrer ».
import { useCallback, useEffect, useRef, useState } from "react";

const mem = {}; // secours si le stockage du navigateur est indisponible
const read = (k) => { try { const v = window.localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return mem[k] || null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, JSON.stringify(v)); } catch { mem[k] = v; } };
const remove = (k) => { try { window.localStorage.removeItem(k); } catch { /* ignore */ } delete mem[k]; };

/**
 * key : identifiant du brouillon (ex. « draft:course:ID » ou « draft:course:new ») ;
 * data : le formulaire ; ready : true quand le contenu enregistré est chargé ; dirty : modifications non enregistrées.
 * Renvoie { pending, savedAt, restore, discard, clear }.
 * - pending : un brouillon plus récent que la version enregistrée, trouvé à l'ouverture ({ data, at }).
 */
export default function useDraft(key, data, { ready = true, dirty = false } = {}) {
  const [pending, setPending] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const checked = useRef(null);

  // À l'ouverture : un brouillon différent de la version enregistrée ?
  useEffect(() => {
    if (!ready || checked.current === key) return;
    checked.current = key;
    const d = read(key);
    if (d && JSON.stringify(d.data) !== JSON.stringify(data)) setPending(d);
    else if (d) remove(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, ready]);

  // Pendant l'écriture : sauvegarde 1,5 s après la dernière modification
  useEffect(() => {
    if (!ready || !dirty || pending) return undefined;
    const t = setTimeout(() => {
      const at = Date.now();
      write(key, { data, at });
      setSavedAt(at);
    }, 1500);
    return () => clearTimeout(t);
  }, [key, data, ready, dirty, pending]);

  const clear = useCallback(() => { remove(key); setSavedAt(null); }, [key]);
  const discard = useCallback(() => { remove(key); setPending(null); }, [key]);
  const restore = useCallback(() => { const p = pending; setPending(null); return p?.data; }, [pending]);

  return { pending, savedAt, restore, discard, clear };
}
